import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  DEFAULT_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  type SupportedLanguage,
} from './types';
import { usePublicTranslations } from './usePublicTranslations';
import { Loader } from '../components';
import { useAuth } from '../auth';

type TranslationMap = Record<string, Record<string, string>>;

interface ITranslateContext {
  lang: SupportedLanguage;
  t: (key: string) => string;
  changeLanguage: (lang: SupportedLanguage) => void;
}

const TranslateContext = createContext<ITranslateContext>({
  lang: DEFAULT_LANGUAGE,
  t: (key: string) => key,
  changeLanguage: () => {},
});

export const TranslateProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [lang, setLang] = useState<SupportedLanguage>(
    (localStorage.getItem(LANGUAGE_STORAGE_KEY) as SupportedLanguage) ||
      DEFAULT_LANGUAGE
  );

  const { data: rawTranslations, isLoading } = usePublicTranslations({
    enabled: !!user,
  });

  const translations = useMemo<TranslationMap>(() => {
    const map: TranslationMap = {};
    for (const tr of rawTranslations) {
      for (const [l, value] of Object.entries(tr.values)) {
        if (!map[l]) map[l] = {};
        map[l][tr.key] = value;
      }
    }
    return map;
  }, [rawTranslations]);

  const t = useCallback(
    (key: string): string =>
      translations[lang]?.[key] ?? translations[DEFAULT_LANGUAGE]?.[key] ?? key,
    [translations, lang]
  );

  const handleChange = (l: SupportedLanguage) => {
    setLang(l);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, l);
  };

  if (isLoading) return <Loader />;

  return (
    <TranslateContext.Provider
      value={{ lang, t, changeLanguage: handleChange }}
    >
      {children}
    </TranslateContext.Provider>
  );
};

export const useTranslate = () => useContext(TranslateContext);
